import React, { useRef, useEffect, useMemo } from 'react';
import { Modal, Button, Row, Col } from 'antd';
import { Form } from 'formik-antd';
import { isNil, flatMap, isEqual } from 'lodash';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';

import cx from 'classnames';

import {
  BOUserStatusEnum,
  FullBOUserResponseModel,
  BOUsersPriceAgreementModel,
  StageStatusEnum,
} from 'api/adminApiClient';
import {
  createRecruiterPriceAgreement,
  changeRecruiterPriceAgreement,
  editPriceAgreement,
} from 'redux/reducers/recruiterSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import SubmitButton from 'components/formik/SubmitButton';
import { getAllStates } from 'redux/reducers/regionSlice';
import {
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import PricingAgreementUploader from './PricingAgreementUploader';
import {
  BOUsersPriceAgreementEditModel,
  ResourceDownloadResponse,
  StateEntity,
} from 'api/backofficeApiClient';
import { BaseModalFooter, BaseModal } from 'components/wrappers/BaseModal';
import { StepChangeRequestModel } from 'utils/profileProgressUtils/generalProgressUtils';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';

import styles from './PricingAgreementModal.module.less';

export enum PricingAgreementType {
  Create = 0,
  Change = 1,
  AdminEdit = 2,
}

type Props = {
  onClose: () => void;
  recruiter?: FullBOUserResponseModel;
  agreementType: PricingAgreementType;
  pricingAgreementFileInfo?: ResourceDownloadResponse;
  onAgreementSent: (stageData: StepChangeRequestModel) => void;
};

interface PricingSet {
  w2Price: number | null;
  placementStates: number[];
}

const emptyPricingSet: PricingSet = { w2Price: null, placementStates: [] };

interface PricingAgreementFormValues {
  agreementType: PricingAgreementType;
  pricingSets: Array<PricingSet>;
  icPrice: number | null;
  agreementPath?: string[];
}

const isAgreementRequired = (agreementType: PricingAgreementType) =>
  agreementType === PricingAgreementType.Create ||
  agreementType === PricingAgreementType.AdminEdit;

const pricingSetValidationSchema = {
  w2Price: Yup.number()
    .nullable()
    .required('Field is required')
    .min(0, 'W2 price should be from 0 to 100')
    .max(100, 'W2 price should be from 0 to 100'),
  placementStates: Yup.array().of(Yup.number()).min(1, 'Field is required'),
};

const validationSchema = Yup.object({
  pricingSets: Yup.array().of(Yup.object(pricingSetValidationSchema)),
  icPrice: Yup.number()
    .nullable()
    .required('Field is required')
    .min(0, 'IC price should be from 0 to 100')
    .max(100, 'IC price should be from 0 to 100'),
  agreementPath: Yup.array()
    .of(Yup.string())
    .when('agreementType', {
      is: isAgreementRequired,
      then: Yup.array().of(Yup.string()).min(1, 'Field is required'),
    }),
});

const getPricingSetStates = (
  states: StateEntity[],
  setIndex: number,
  formPricingSets: Array<PricingSet>,
) => {
  const statesInOtherSets = flatMap(formPricingSets, (set, index) =>
    setIndex === index ? [] : set.placementStates,
  );
  const result = states.filter(
    (state) => !statesInOtherSets.includes(state.id as number),
  );

  return result;
};

export default function PricingAgreementModal(props: Props) {
  const {
    onClose,
    onAgreementSent,
    recruiter,
    agreementType,
    pricingAgreementFileInfo,
  } = props;
  const dispatch = useAppDispatch();
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const formRef: any = useRef();
  const initialValues = getInitialValues();
  const isAdminEdit = agreementType === PricingAgreementType.AdminEdit;

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === countryUSAid),
    [states],
  );

  useEffect(() => {
    dispatch(getAllStates());
  }, [dispatch]);

  function getInitialValues(): PricingAgreementFormValues {
    if (
      agreementType === PricingAgreementType.Create ||
      agreementType === PricingAgreementType.Change
    ) {
      return {
        agreementType,
        pricingSets: [emptyPricingSet],
        icPrice: null,
        agreementPath: [],
      };
    }

    const pricingSets: Array<PricingSet> = recruiter?.businessInfo
      ?.priceAgreement?.w2InfoList?.length
      ? (recruiter?.businessInfo?.priceAgreement?.w2InfoList).map((w2Info) => ({
          w2Price: !isNil(w2Info.w2Price) ? w2Info.w2Price : null,
          placementStates: (w2Info.placementStates || []).map(
            (state) => state.id as number,
          ),
        }))
      : [emptyPricingSet];
    const icPrice = !isNil(recruiter?.businessInfo?.priceAgreement?.icPrice)
      ? (recruiter?.businessInfo?.priceAgreement?.icPrice as number)
      : null;
    const agreementPath: string[] = recruiter?.businessInfo?.priceAgreement
      ?.fileKey
      ? [recruiter?.businessInfo?.priceAgreement?.fileKey]
      : [];

    return {
      agreementType,
      pricingSets,
      icPrice,
      agreementPath,
    };
  }

  const getSubmitData = (values: PricingAgreementFormValues) => {
    const { pricingSets, icPrice, agreementPath } = values;

    if (!pricingSets.length || isNil(icPrice)) return;

    const w2InfoList = pricingSets.map((item) => ({
      w2Price: item.w2Price as number,
      placementStateIds: item.placementStates,
    }));

    const createData: BOUsersPriceAgreementModel = {
      w2InfoList,
      icPrice,
      companyId: recruiter?.companyId,
      priceAgreementFileKey: agreementPath?.length
        ? agreementPath[0]
        : undefined,
    };
    const changeData: BOUsersPriceAgreementEditModel = {
      w2InfoList,
      icPrice,
    };

    switch (agreementType) {
      case PricingAgreementType.Create:
      case PricingAgreementType.AdminEdit:
        return createData;

      case PricingAgreementType.Change:
        return changeData;
    }
  };

  const getAction = () => {
    switch (agreementType) {
      case PricingAgreementType.Create:
        return createRecruiterPriceAgreement;

      case PricingAgreementType.Change:
        return changeRecruiterPriceAgreement;

      case PricingAgreementType.AdminEdit:
        return editPriceAgreement;
    }
  };

  const handleSubmit = async (values: PricingAgreementFormValues) => {
    // https://mybasepay.atlassian.net/browse/MBO-1832
    // Do not send pricing agreement if the data hasn't changed
    if (isAdminEdit) {
      const areValuesEqual = isEqual(initialValues, values);

      if (areValuesEqual) return onClose();
    }

    const data = getSubmitData(values);
    const correctAction = getAction();

    if (!data) return;

    const result = await dispatch(correctAction(data));
    if (correctAction.fulfilled.match(result)) {
      await onAgreementSent({
        stage: BOUserStatusEnum.PriceAgreementSent,
        stageStatus: StageStatusEnum.Completed,
      });
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title="Pricing agreement"
      open={true}
      closable={true}
      onCancel={handleCloseModal}
      destroyOnClose
      maxWidth={468}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div>
                <FieldArray
                  name="pricingSets"
                  render={(arrayHelpers) => (
                    <>
                      {!!formik.values.pricingSets.length &&
                        formik.values.pricingSets.map((set, index: number) => (
                          <div key={index}>
                            <Row gutter={20}>
                              <Col xs={24}>
                                <FormItem
                                  label="W2 price % *"
                                  name={`pricingSets.${index}.w2Price`}
                                  data-id={`w2PriceField-${index}`}
                                >
                                  <InputNumber
                                    precision={2}
                                    name={`pricingSets.${index}.w2Price`}
                                    placeholder="Enter W2 price"
                                    className={styles.input}
                                  />
                                </FormItem>
                              </Col>
                            </Row>

                            <Row gutter={20}>
                              <Col xs={24}>
                                <FormItem
                                  label="States that W2 price applies to *"
                                  name={`pricingSets.${index}.placementStates`}
                                  data-id={`placementStatesField-${index}`}
                                >
                                  <FormikSelectWithLoading
                                    isLoading={isStatesLoading}
                                    name={`pricingSets.${index}.placementStates`}
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select states"
                                    optionFilterProp="children"
                                    key={`set-${index}-${
                                      getPricingSetStates(
                                        states,
                                        index,
                                        formik.values.pricingSets,
                                      ).length
                                    }`}
                                  >
                                    {getPricingSetStates(
                                      filteredStates,
                                      index,
                                      formik.values.pricingSets,
                                    ).map((state) => (
                                      <FormikSelectWithLoading.Option
                                        key={`select-place-${state.id}`}
                                        value={state.id || -1}
                                      >
                                        {state.name}
                                      </FormikSelectWithLoading.Option>
                                    ))}
                                  </FormikSelectWithLoading>
                                </FormItem>
                              </Col>
                            </Row>

                            <Col flex="0 0 auto">
                              <div className={styles.actionButtons}>
                                {formik.values.pricingSets.length - 1 ===
                                  index && (
                                  <Button
                                    className={cx('borderless-btn', styles.btn)}
                                    onClick={() =>
                                      arrayHelpers.push(emptyPricingSet)
                                    }
                                    icon={
                                      <span className="icon icon-plus-filled"></span>
                                    }
                                  />
                                )}
                                {formik.values.pricingSets.length > 1 && (
                                  <Button
                                    className={cx('borderless-btn', styles.btn)}
                                    onClick={() => arrayHelpers.remove(index)}
                                    icon={
                                      <span className="icon icon-closed-filled" />
                                    }
                                  />
                                )}
                              </div>
                            </Col>
                          </div>
                        ))}
                    </>
                  )}
                />
              </div>

              <Row gutter={20}>
                <Col xs={24}>
                  <FormItem
                    data-id="icPriceField"
                    label="IC price % *"
                    name="icPrice"
                  >
                    <InputNumber
                      name="icPrice"
                      placeholder="Enter IC price"
                      precision={2}
                    />
                  </FormItem>
                </Col>
              </Row>

              {isAgreementRequired(agreementType) && (
                <>
                  <div className="modal-title">Upload agreement *</div>

                  <Row gutter={[0, 40]} justify="center">
                    <Col xs={24}>
                      <div className="upload-wrapper">
                        <FormItem
                          name="agreementPath"
                          showValidateError={false}
                        >
                          <PricingAgreementUploader
                            defaultFile={pricingAgreementFileInfo}
                          />
                        </FormItem>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            <BaseModalFooter>
              <div>
                <Button
                  data-id="closePricingAgreementModalBtn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <SubmitButton
                  type="primary"
                  data-id="submitPricingAgreementBtn"
                >
                  Send
                </SubmitButton>
              </div>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
